/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IColumn, SelectionMode } from '@fluentui/react/lib/DetailsList';
import { Link } from '@fluentui/react/lib/Link';
import { Stack } from '@fluentui/react/lib/Stack';
import orderBy from 'lodash.orderby';
import { observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { ConfirmationModal } from 'src/core/components/ConfirmationModal';
import { DeletePromptModal } from 'src/core/components/DeletePromptModal';
import { EditableTitle } from 'src/core/components/EditableTitle';
import { EmptyStateView } from 'src/core/components/EmptyStateView';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { LuisText } from 'src/core/components/LuisText';
import { Page } from 'src/core/components/Page';
import { SortKeyType } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useSelection } from 'src/core/hooks/useSelection';
import { useUpdateState } from 'src/core/hooks/useUpdateState';
import { useZoomLevel } from 'src/core/hooks/useZoomLevel';
import { logButtonClick } from 'src/core/services/telemetry/eventsNamesTemplate';
import { getStylistV2 } from 'src/core/themes/luisStylist';
import { routeTable } from 'src/core/utils/routeTable';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';
import { substringSearchStrategy } from 'src/core/utils/searchStrategies/substringSearchStrategy';
import { createKeyedStringNormalizer } from 'src/core/utils/strings';
import { useModelsEvaluationData } from 'src/customText/core/hooks/useModelsEvaluationData';
import { ModelDetailsListCommandBar } from 'src/modules/modelDetails/components/ModelDetailsListCommandBar';
import { ModelDetailsListTable, ModelDetailsObject } from 'src/modules/modelDetails/components/ModelDetailsListTable';

const messages = defineMessages({
    pageTitle: { id: 'customText.customTextModelDetailsListPage.pageTitle', defaultMessage: 'Model performance' },
    exportRunningToast: { id: 'customText.customTextModelDetailsListPage.exportRunningToast', defaultMessage: 'Exporting model data ...' },
    loadModelDataModelTitle: { id: 'customText.customTextModelDetailsListPage.loadModelDataModelTitle', defaultMessage: 'Load model data' },
    refreshEvaluationTitle: {
        id: 'customText.customTextModelDetailsListPage.refreshEvaluationTitle',
        defaultMessage: 'Refresh evaluation'
    },
    deletingModelTitle: { id: 'customText.customTextModelDetailsListPage.deletingModelTitle', defaultMessage: 'Delete selected models(s)' },
    deletingModelBody: {
        id: 'customText.customTextModelDetailsListPage.deletingModelBody',
        defaultMessage: 'Are you sure you want to delete the selected model(s)?'
    },
    failedDeletingModels: {
        id: 'customText.customTextModelDetailsListPage.failedDeletingModels',
        defaultMessage: '{failedCount} models failed.'
    },
    extractionDescription: {
        id: 'customText.customTextModelDetailsListPage.extractionDescription',
        defaultMessage:
            'Select a model to view details about how it performed. Review the precision and recall scores for each entity type as well as the confusion matrix which compares predictions against labeled testing data.'
    },
    classificationDescription: {
        id: 'customText.customTextModelDetailsListPage.classificationDescription',
        defaultMessage:
            'Select a model to view details about how it performed. Review the precision and recall scores for each class as well as the confusion matrix which compares predictions against labeled testing data.'
    },
    learnMore: {
        id: 'customText.customTextModelDetailsListPage.learnMore',
        defaultMessage: 'Learn more about how to evaluate your model'
    },
    loadModelDataModelBody1: {
        id: 'customText.customTextModelDetailsListPage.loadModelDataModelBody1',
        defaultMessage:
            'Loading this model’s data will replace all data including the classes/entities and labels in your project with the data that was used to train model'
    },
    loadModelDataModelBody2: {
        id: 'customText.customTextModelDetailsListPage.loadModelDataModelBody2',
        defaultMessage: 'If you have not saved the current state of your project to a model and would like to,'
    },
    loadModelDataModelBody3: {
        id: 'customText.customTextModelDetailsListPage.loadModelDataModelBody3',
        defaultMessage: 'or'
    },
    testSetTitle: {
        id: 'customText.customTextModelDetailsListPage.testSetTitle',
        defaultMessage: 'Test Set'
    },
    refreshEvaluationBody1: {
        id: 'customText.customTextModelDetailsListPage.refreshEvaluationBody1',
        defaultMessage: 'Running will use the current data in your '
    },
    refreshEvaluationBody2: {
        id: 'customText.customTextModelDetailsListPage.refreshEvaluationBody2',
        defaultMessage: 'to evaluate the model '
    },
    refreshEvaluationBody3: {
        id: 'customText.customTextModelDetailsListPage.refreshEvaluationBody3',
        defaultMessage: '. If your model already has an evaluation report, it will be overwritten.'
    },
    refreshEvaluationBody4: {
        id: 'customText.customTextModelDetailsListPage.refreshEvaluationBody4',
        defaultMessage:
            'Any new intents or entities in the test set that were not  present when the model was trained will not be predicted.'
    },
    refreshEvaluationButton: {
        id: 'customText.customTextModelDetailsListPage.refreshEvaluationButton',
        defaultMessage: 'Evaluate'
    },
    trainModelLink: {
        id: 'customText.customTextModelDetailsListPage.trainModelLink',
        defaultMessage: 'train a new model'
    },
    exportProjectLink: {
        id: 'customText.customTextModelDetailsListPage.exportProjectLink',
        defaultMessage: 'export your project now.'
    },
    exportCompletedToast: {
        id: 'customText.customTextModelDetailsListPage.exportCompletedToast',
        defaultMessage: 'Export completed successfully'
    },
    loadModelDataRunningToast: {
        id: 'customText.customTextModelDetailsListPage.loadModelDataRunningToast',
        defaultMessage: 'Loading model data ...'
    },
    refreshEvaluationRunningToast: {
        id: 'customText.customTextModelDetailsListPage.refreshEvaluationRunningToast',
        defaultMessage: 'Evaluation in progress'
    },
    loadModelDataCompletedToast: {
        id: 'customText.customTextModelDetailsListPage.loadModelDataCompletedToast',
        defaultMessage: 'Loading model data completed successfully'
    },
    refreshEvaluationCompletedToast: {
        id: 'customText.customTextModelDetailsListPage.refreshEvaluationCompletedToast',
        defaultMessage: 'Evaluation completed successfully'
    }
});

const { styleDiv } = getStylistV2('ModelDetailsListPage');

const ModelsTableWindow = styleDiv('ModelsTableWindow', { height: '100%', overflowY: 'auto' });
const defaultModalsState = { isDeleteModelModalHidden: true };

export const CustomTextModelDetailsListPage = observer(() => {
    const history = useHistory();
    const { localize } = useLocalization();
    const { serviceName } = getPortalFromUrl();
    const { dispatcher, rootStore } = useGlobalState();
    const { isAccessibleMode } = useZoomLevel();

    const [query, setQuery] = React.useState('');
    const { selection, selectedItems } = useSelection<ModelDetailsObject>();

    const [modalsState, setModalsState] = useUpdateState(defaultModalsState);
    const [isLoadModelDataModalHidden, setIsLoadModelDataModalHidden] = React.useState(true);
    const [isRefreshEvaluationModalHidden, setIsRefreshEvaluationModalHidden] = React.useState(true);

    const [sortKey, setSortKey] = React.useState<SortKeyType<ModelDetailsObject>>({ key: 'name', order: 'asc' });

    const defaultOperationsTracker = useProgressTracker({
        onComplete: () => {
            setModalsState(defaultModalsState);
            selection.setAllSelected(false);
        }
    });

    const loadSnapshotTracker = useProgressTracker({
        onComplete: () => {
            setIsLoadModelDataModalHidden(true);
            selection.setAllSelected(false);
            history.push(
                routeTable.customText.dataTaggingPage(currentCustomTextProjectStore.id, currentCustomTextProjectStore.projectKind)
            );
        }
    });

    const refreshEvaluationTracker = useProgressTracker();

    const exportSnapshotTracker = useProgressTracker({
        onComplete: () => {
            setIsLoadModelDataModalHidden(true);
            selection.setAllSelected(false);
        }
    });

    const { currentCustomTextProjectStore } = rootStore;
    const { modelsEvaluationMap } = currentCustomTextProjectStore;
    const { name: projectName, trainedModels, projectKind, id: projectId } = currentCustomTextProjectStore;
    const isExtractionProject = currentCustomTextProjectStore.projectKind === 'CustomEntityRecognition';

    const models = trainedModels.items ?? [];
    const isInEmptyState = trainedModels.tracker.isCompleted && models.length === 0;
    const evaluationJobStore = rootStore.currentCustomTextProjectStore?.evaluationJobStore;
    const isEvaluationJobRunning =
        evaluationJobStore && evaluationJobStore?.status !== 'succeeded' && evaluationJobStore?.status !== 'failed';

    const isTrainingJobRunning = rootStore.currentCustomTextProjectStore?.trainingServiceStatus !== 'ready';
    useModelsEvaluationData(models);

    const mappedModels = models.map<ModelDetailsObject>(model => ({
        name: model.name,
        f1Score: modelsEvaluationMap.get(model.name)?.f1Score,
        hasSnapshot: model.hasSnapshot,
        expiryDate: model.expirationDate,
        isLoading: false,
        isClickable: model.expirationDate > new Date()
    }));

    const filteredModels = orderBy(
        substringSearchStrategy(mappedModels, query, 'name'),
        [createKeyedStringNormalizer(sortKey.key)],
        [sortKey.order]
    );

    const handleOpenModel = (modelName: string): void =>
        history.push(routeTable.customText.modelDetailsPage(projectName, projectKind, modelName));

    const onSortColumn = (c: IColumn) =>
        setSortKey({ key: c.key as keyof ModelDetailsObject, order: c.isSortedDescending ? 'asc' : 'desc' });

    const handleDeleteTrainedModels = () => {
        dispatcher.dispatch('deleteCustomTextTrainedModels', {
            toastData: {},
            tracker: defaultOperationsTracker,
            projectName,
            modelNames: selectedItems.map(item => item.name),
            generateMultiFailureMessage: (failedCount: number) => localize(messages.failedDeletingModels, { failedCount })
        });
    };

    const handleLoadModelData = () => {
        dispatcher.dispatch('loadCustomTextSnapshotModel', {
            projectName,
            trainedModelName: selectedItems[0].name,
            tracker: loadSnapshotTracker,
            toastData: {
                running: { title: localize(messages.loadModelDataRunningToast) },
                completed: { title: localize(messages.loadModelDataCompletedToast) }
            }
        });
    };

    const handleRefreshEvaluation = () => {
        dispatcher.dispatch('refreshEvaluationCustomText', {
            projectName,
            trainedModelName: selectedItems[0].name,
            tracker: refreshEvaluationTracker
        });
        setIsRefreshEvaluationModalHidden(true);
        selection.setAllSelected(false);
    };

    const handleExportModelData = (isExportModel: boolean) => {
        setIsLoadModelDataModalHidden(true);
        selection.setAllSelected(false);

        dispatcher.dispatch('exportCustomTextProject', {
            projectName,
            trainedModelName: isExportModel ? selectedItems[0].name : null,
            tracker: exportSnapshotTracker,
            toastData: {
                running: { title: localize(messages.exportRunningToast) },
                completed: { title: localize(messages.exportCompletedToast) }
            }
        });
    };

    const subtitle = (
        <>
            {localize(isExtractionProject ? messages.extractionDescription : messages.classificationDescription)}
            &nbsp;
            <ExternalLink href={isExtractionProject ? 'https://aka.ms/ct-evaluation-concepts' : 'https://aka.ms/ctc-evaluation-concepts'}>
                {localize(messages.learnMore)}
            </ExternalLink>
        </>
    );

    const handleDeleteModel = () => {
        logButtonClick(serviceName, 'ModelPerfomance', 'DeleteModel', null, projectId);
        setModalsState({ isDeleteModelModalHidden: false });
    };

    return (
        <Page
            size="container-fluid"
            pageName="ModelDetailsListPage"
            title={localize(messages.pageTitle)}
            data-id="CustomTextModelDetailsList">
            <EditableTitle title={localize(messages.pageTitle)} subtitle={subtitle} />

            <ModelDetailsListCommandBar
                query={query}
                onQueryChange={setQuery}
                selectedItems={selectedItems}
                onDeleteModels={handleDeleteModel}
                onExportModel={() => handleExportModelData(true)}
                onLoadModel={() => setIsLoadModelDataModalHidden(false)}
                onRefreshEvaluation={() => setIsRefreshEvaluationModalHidden(false)}
                isEvaluationOrTrainingRunning={isEvaluationJobRunning || isTrainingJobRunning}
            />

            {isInEmptyState ? (
                <EmptyStateView />
            ) : (
                <ModelsTableWindow data-is-scrollable={!isAccessibleMode}>
                    <ModelDetailsListTable
                        sortKey={sortKey}
                        selection={selection}
                        items={filteredModels}
                        onSortColumn={onSortColumn}
                        respectViewportHeight={false}
                        onModelClick={handleOpenModel}
                        areItemsFiltered={Boolean(query)}
                        selectionMode={SelectionMode.multiple}
                        isModelLinkAttributeName="is-model-link"
                        overallItemsCount={filteredModels.length}
                        isLoading={trainedModels.tracker.isRunning || defaultOperationsTracker.isRunning}
                        hiddenColumnKeys={['entityF1Score', 'trainingUtterances', 'testingUtterances']}
                    />
                </ModelsTableWindow>
            )}

            <DeletePromptModal
                onDelete={handleDeleteTrainedModels}
                disabled={defaultOperationsTracker.isRunning}
                hidden={modalsState.isDeleteModelModalHidden}
                dialogContentProps={{ title: localize(messages.deletingModelTitle) }}
                onDismiss={() => setModalsState({ isDeleteModelModalHidden: true })}>
                <LuisText>{localize(messages.deletingModelBody)}</LuisText>
            </DeletePromptModal>

            <ConfirmationModal
                onConfirm={handleLoadModelData}
                disabled={loadSnapshotTracker.isRunning}
                hidden={isLoadModelDataModalHidden}
                dialogContentProps={{ title: localize(messages.loadModelDataModelTitle) }}
                onDismiss={() => setIsLoadModelDataModalHidden(true)}>
                <Stack verticalFill verticalAlign="center" horizontalAlign="center" tokens={{ childrenGap: '22px' }}>
                    <LuisText>
                        {localize(messages.loadModelDataModelBody1)}
                        &nbsp;
                        {selectedItems?.[0]?.name}.
                    </LuisText>
                    <LuisText>
                        <LuisText>{localize(messages.loadModelDataModelBody2)}</LuisText>
                        &nbsp;
                        <Link
                            onClick={() =>
                                history.push(
                                    routeTable.customText.trainingPage(
                                        currentCustomTextProjectStore.id,
                                        currentCustomTextProjectStore.projectKind
                                    )
                                )
                            }>
                            {localize(messages.trainModelLink)}
                        </Link>
                        &nbsp;
                        <LuisText>{localize(messages.loadModelDataModelBody3)}</LuisText>
                        &nbsp;
                        <Link onClick={() => handleExportModelData(false)}>{localize(messages.exportProjectLink)}</Link>
                    </LuisText>
                </Stack>
            </ConfirmationModal>

            <ConfirmationModal
                onConfirm={handleRefreshEvaluation}
                hidden={isRefreshEvaluationModalHidden}
                dialogContentProps={{ title: localize(messages.refreshEvaluationTitle) }}
                submitButtonText={localize(messages.refreshEvaluationButton)}
                onDismiss={() => setIsRefreshEvaluationModalHidden(true)}>
                <Stack verticalFill verticalAlign="center" horizontalAlign="center" tokens={{ childrenGap: '22px' }}>
                    <LuisText>
                        <LuisText>{localize(messages.refreshEvaluationBody1)}</LuisText>
                        <Link
                            onClick={() =>
                                history.push(
                                    routeTable.customText.dataTaggingPage(
                                        currentCustomTextProjectStore.id,
                                        currentCustomTextProjectStore.projectKind
                                    ),
                                    {
                                        filter: 'testing'
                                    }
                                )
                            }>
                            {localize(messages.testSetTitle)}
                        </Link>
                        &nbsp;
                        <LuisText>{localize(messages.refreshEvaluationBody2)}</LuisText>
                        <LuisText fontWeight={600}>{selectedItems?.[0]?.name}</LuisText>
                        {localize(messages.refreshEvaluationBody3)}
                    </LuisText>
                    <LuisText>{localize(messages.refreshEvaluationBody4)}</LuisText>
                </Stack>
            </ConfirmationModal>
        </Page>
    );
});
